import camelCase from 'lodash/camelCase';
import type { MaterialV1, NewMaterialV1 } from '@ilteducation/content-registry-types';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import usePatchDiffer from "@/foundations/helpers/uss-pacth-differ";

const useMaterialUtil = () => {
  const availableSubscriptions = defaultSubscriptions;

  const newMaterial = (overrides?: any): NewMaterialV1 => ({
    type: 'MaterialV1',
    realm: 'se',
    availabilityRealms: ['se'],
    language: 'sv', // Using ISO 639-1
    resourceType: 'pdf',
    title: '',
    originalTitle: '',
    description: '',
    keywords: [],
    properties: {
      audiences: [],
      forChapterBook: false,
      forEasyToRead: false,
      forPictureBook: false,
      forBoardBook: false,
      forFactBook: false,
    },
    accessRules: { subscriptionType: [], roles: [] },
    ages: { min: 0, max: 18 },
    managementNotes: undefined,
    ...overrides,
  });

  const patchMaterial = (original: MaterialV1, updated: MaterialV1): Partial<MaterialV1> => {
    return usePatchDiffer<MaterialV1>(original, updated);
  };

  const languageRealm: Record<string, string> = {
    sv: 'se',
    uk: 'uk',
    us: 'us',
    de: 'de',
    fi: 'fi',
    fr: 'fr',
    nb: 'no',
    nn: 'no',
    no: 'no',
    nl: 'nl',
  };

  const normalizedInput = (input: any, excludeKeys: string[] = []): any =>
    Object.keys(input).reduce((acc, key) => {
      const camelKey = camelCase(key);
      const value = input[key];

      return {
        ...acc,
        [camelKey]:
          typeof value === 'string' && value.includes(',') && !excludeKeys.includes(camelKey)
            ? value.split(', ').map((item) => item.trim())
            : value,
      };
    }, {});

  const normalizedArrayValue = (input: any, transformFn = (value: any) => value.toLowerCase()) => {
    if (!input || (typeof input === 'string' && input.trim() === '')) {
      return [];
    }
    if (Array.isArray(input)) {
      return input.map((item) => transformFn(item));
    }
    return [transformFn(input)];
  };

  const extractInParentheses = (value?: string): string => {
    if (!value) return '';
    const match = value.match(/\(([^)]+)\)/);
    return match ? match[1] : value;
  };

  const someOnFirstWord = (value: string, words?: string[]): boolean =>
    Array.from(words || []).some((word) => word.split(' ')[0].toLowerCase() === value);

  const parseMetaData = (object: any): NewMaterialV1 => {
    const {
      markets,
      title,
      description,
      keywords,
      language,
      subscriptions,
      agesGlobal,
      userTypesGlobal,
      audienceGlobal,
      bookTypesGlobal,
    } = normalizedInput(object, ['title', 'description', 'language']);

    return {
      ...newMaterial({
        title,
        language: extractInParentheses(language),
        realm: extractInParentheses(markets[0]),
        availabilityRealms: normalizedArrayValue(markets.slice(1), (value) => extractInParentheses(value)),
        description,
        keywords: normalizedArrayValue(keywords),
        accessRules: {
          subscriptionType: normalizedArrayValue(subscriptions)
            .map((sub) => availableSubscriptions.find((aSub) => aSub.subscription.toLowerCase() === sub)?.value)
            .filter(Boolean),
          roles: normalizedArrayValue(userTypesGlobal),
        },
        properties: {
          audiences: normalizedArrayValue(audienceGlobal),
          forChapterBook: someOnFirstWord('chapter', normalizedArrayValue(bookTypesGlobal)),
          forEasyToRead: someOnFirstWord('easy', normalizedArrayValue(bookTypesGlobal)),
          forPictureBook: someOnFirstWord('picture', normalizedArrayValue(bookTypesGlobal)),
          forBoardBook: someOnFirstWord('board', normalizedArrayValue(bookTypesGlobal)),
          forFactBook: someOnFirstWord('fact', normalizedArrayValue(bookTypesGlobal)),
        },
        ages: agesGlobal
          ? {
              min: parseInt(agesGlobal.split('-')[0], 10),
              max: parseInt(agesGlobal.split('-')[1], 10),
            }
          : undefined,
      }),
    };
  };

  return {
    newMaterial,
    patchMaterial,
    languageRealm,
    parseMetaData,
  };
};

export default useMaterialUtil;
